import {CM_LOGGER_PICTURES} from "@app/common/services/logging"
import {SelectionChange} from "@angular/cdk/collections"
import {CdkTreeModule, NestedTreeControl} from "@angular/cdk/tree"
import {Component, DestroyRef, effect, inject, input, model, OnInit, signal, ChangeDetectionStrategy} from "@angular/core"
import {outputToObservable, takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop"
import {FormControl, ReactiveFormsModule} from "@angular/forms"
import {MatAutocompleteModule, MatAutocompleteSelectedEvent} from "@angular/material/autocomplete"
import {MatDialog} from "@angular/material/dialog"
import {MatInputModule} from "@angular/material/input"
import {MatMenuModule} from "@angular/material/menu"
import {MatSnackBar} from "@angular/material/snack-bar"
import {MatTooltipModule} from "@angular/material/tooltip"
import {MatTreeNestedDataSource} from "@angular/material/tree"
import {ActivatedRoute, Router, RouterModule} from "@angular/router"
import {
    ProjectTreeProjectFragment,
    ProjectTreeSetFragment,
    ProjectTreeUserFragment,
} from "@app/platform/components/pictures/project-tree/project-tree.generated"
import {IsDefined, IsNonNull} from "@cm/utils/filter"
import {DataObjectThumbnailComponent} from "@common/components/data-object-thumbnail/data-object-thumbnail.component"
import {DialogRenameComponent} from "@common/components/dialogs/dialog-rename/dialog-rename.component"
import {ListItemComponent} from "@common/components/item"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {ThumbnailLayout} from "@common/models/enums/thumbnail-layout"
import {Settings} from "@common/models/settings/settings"
import {AuthService} from "@common/services/auth/auth.service"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {RefreshService} from "@common/services/refresh/refresh.service"
import {BasicOrganizationInfoFragment} from "@common/services/organizations/organizations.generated"
import {DownloadResolution} from "@generated"
import {UtilsService} from "@legacy/helpers/utils"
import {
    ProjectTreeCreateProjectGQL,
    ProjectTreeCreateSetGQL,
    ProjectTreeDeleteProjectGQL,
    ProjectTreeDeleteSetGQL,
    ProjectTreePicturesGQL,
    ProjectTreeProjectSetsGQL,
    ProjectTreeProjectsGQL,
    ProjectTreeSetGQL,
    ProjectTreeUpdatePictureGQL,
    ProjectTreeUpdateProjectGQL,
    ProjectTreeUpdateSetGQL,
    ProjectTreeVisibleUsersGQL,
} from "@platform/components/pictures/project-tree/project-tree.generated"
import {OrganizationDetailsFragment} from "@platform/pages/organizations/organizations-page.generated"
import {BehaviorSubject, combineLatest, from, of, switchMap} from "rxjs"

// Sets are loaded lazily when a project is expanded (see loadSetsForProject), so a project node
// carries its own sets array plus a flag recording whether that fetch has completed.
type ProjectTreeNode = ProjectTreeProjectFragment & {sets: ProjectTreeSetFragment[]; setsLoaded: boolean}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-project-tree",
    templateUrl: "./project-tree.component.html",
    styleUrls: ["./project-tree.component.scss"],
    imports: [
        MatInputModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        CdkTreeModule,
        RouterModule,
        MatTooltipModule,
        MatMenuModule,
        ListItemComponent,
        DataObjectThumbnailComponent,
    ],
})
export class ProjectTreeComponent implements OnInit {
    private readonly logger = inject(CM_LOGGER_PICTURES)
    settings = Settings
    readonly $currentProjectId = model<string | undefined>(undefined, {alias: "currentProjectId"})
    readonly $currentSetId = model<string | undefined>(undefined, {alias: "currentSetId"})
    readonly $organization = input<{id: string} | undefined>(undefined, {alias: "organization"})
    readonly $dataSource = signal(new MatTreeNestedDataSource<ProjectTreeNode>())
    readonly $projects = signal<ProjectTreeNode[]>([])
    // Ids of projects whose sets are currently being fetched, used to show an inline loading indicator.
    readonly $loadingProjectIds = signal(new Set<string>())
    treeControl = new NestedTreeControl<ProjectTreeNode | ProjectTreeSetFragment, string>((node) => (node.__typename === "Project" ? node.sets : undefined), {
        trackBy: ({id}) => id,
    })
    // A project can always hold sets (they load lazily on expand), so every project node is expandable.
    hasChild = (_: number, node: ProjectTreeNode | ProjectTreeSetFragment) => node.__typename === "Project"
    customerInputControl = new FormControl<string | Pick<BasicOrganizationInfoFragment, "name" | "id"> | null>(null)
    readonly $filteredOrganizations = signal<BasicOrganizationInfoFragment[]>([])
    visibleUsers: ProjectTreeUserFragment[] | null = null
    public userIsStaff = false

    readonly organizations = inject(OrganizationsService)
    readonly permission = inject(PermissionsService)
    readonly refresh = inject(RefreshService)
    $can = this.permission.$to

    readonly visibleUsersGql = inject(ProjectTreeVisibleUsersGQL)
    readonly setGql = inject(ProjectTreeSetGQL)
    readonly projectsGql = inject(ProjectTreeProjectsGQL)
    readonly projectSetsGql = inject(ProjectTreeProjectSetsGQL)
    readonly updateProjectGql = inject(ProjectTreeUpdateProjectGQL)
    readonly updateSetGql = inject(ProjectTreeUpdateSetGQL)
    readonly createProjectGql = inject(ProjectTreeCreateProjectGQL)
    readonly createSetGql = inject(ProjectTreeCreateSetGQL)
    readonly deleteProjectGql = inject(ProjectTreeDeleteProjectGQL)
    readonly deleteSetGql = inject(ProjectTreeDeleteSetGQL)
    readonly picturesGql = inject(ProjectTreePicturesGQL)
    readonly updatePictureGql = inject(ProjectTreeUpdatePictureGQL)

    protected readonly DownloadResolution = DownloadResolution
    readonly dialog = inject(MatDialog)
    readonly authService = inject(AuthService)
    private readonly route = inject(ActivatedRoute)
    readonly router = inject(Router)
    private readonly snackBar = inject(MatSnackBar)
    readonly utils = inject(UtilsService)
    private readonly destroyRef = inject(DestroyRef)

    constructor() {
        // Single source of truth: render the tree from $projects whenever it changes. Clearing the data to []
        // before re-setting forces the CDK nested tree to re-read children even when node ids are unchanged
        // (https://github.com/angular/components/issues/11381), and keeps dataNodes in sync so lookups never
        // see stale nodes. No handler touches the data source or dataNodes directly — setting $projects is
        // the only thing needed to keep the display consistent with the data.
        effect(() => {
            const projects = this.$projects()
            const dataSource = this.$dataSource()
            dataSource.data = []
            dataSource.data = projects
            this.treeControl.dataNodes = projects
        })

        // Load (or reload) the project list when the organization changes or a reload is requested.
        combineLatest([toObservable(this.$organization), this.reload$])
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(([organization]) => {
                    if (!organization) return of(null)
                    this.customerInputControl.setValue(organization)
                    return from(this.fetchProjects(organization.id))
                }),
            )
            .subscribe((projects) => {
                if (!projects) return
                // A fresh list starts collapsed: expansion state is id-keyed and would otherwise survive the
                // reload, leaving projects marked expanded but with their (now unloaded) sets not re-fetched.
                this.treeControl.expansionModel.clear()
                this.$projects.set(projects)
                void this.expandSelectedProject()
            })

        // Expand the project that owns the currently selected set (e.g. on deep-link navigation).
        toObservable(this.$currentSetId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => void this.expandSelectedProject())
    }

    reload$ = new BehaviorSubject<void>(undefined)
    public reload() {
        this.reload$.next()
    }

    ngOnInit() {
        fetchThrowingErrors(this.visibleUsersGql)({}).then(({users}) => {
            this.visibleUsers = users.filter(IsDefined)
        })
        this.userIsStaff = this.authService.isStaff()
        this.customerInputControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
            this.$filteredOrganizations.set(this.filterCustomers(typeof value === "string" ? value : ""))
        })
        this.treeControl.expansionModel.changed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => this.onExpandedChanged(data))
    }

    private _isCustomerDropdownOpened = false

    public customerAutocompleteOpened() {
        this._isCustomerDropdownOpened = true
        if (!this._isCustomerAutocompleteInputInFocus) {
            this.customerInputControl.setValue("")
        }
    }

    public customerAutocompleteClosed() {
        if (!this._isCustomerAutocompleteInputInFocus) {
            this.customerInputControl.setValue(this.$organization() ?? "")
        }
        this._isCustomerDropdownOpened = false
    }

    private _isCustomerAutocompleteInputInFocus = false

    public customerAutocompleteInputFocusin() {
        if (!this._isCustomerDropdownOpened) {
            this.customerInputControl.setValue("")
        }
        this._isCustomerAutocompleteInputInFocus = true
    }

    public customerAutocompleteInputFocusout() {
        if (!this._isCustomerDropdownOpened) {
            this.customerInputControl.setValue(this.$organization() ?? "")
        }
        this._isCustomerAutocompleteInputInFocus = false
    }

    private filterCustomers(value: string): BasicOrganizationInfoFragment[] {
        const filterValue = value.toLocaleLowerCase()
        return (
            this.organizations.$all()?.filter((organization) => organization.isActive && organization.name?.toLocaleLowerCase()?.startsWith(filterValue)) ?? []
        )
    }

    protected customerDisplayFunction(organization: OrganizationDetailsFragment): string {
        return organization?.name ?? ""
    }

    protected async customerSelected(event: MatAutocompleteSelectedEvent) {
        const organizationId = event.option.value.id
        if (this.$organization()?.id === organizationId) {
            return
        }
        const queryParams = {tab: "projects", organizationId}
        await this.router.navigate([], {queryParams: queryParams})
    }

    // Expand a project and load its sets. loadSetsForProject is idempotent, so calling it here — rather than
    // relying only on the expansionModel.changed event — ensures sets load even when the project is already
    // expanded (expand() is then a no-op that fires no event), e.g. after a reload re-applies the selection.
    private expandProject(project: ProjectTreeNode) {
        this.treeControl.expand(project)
        void this.loadSetsForProject(project)
    }

    async expandSelectedProject() {
        const currentProjectId = this.$currentProjectId()
        if (currentProjectId) {
            const project = this.getProjectNode(currentProjectId)
            if (project) {
                this.expandProject(project)
                return
            }
        }
        // No project selected directly (or it wasn't found): resolve the owning project from the current set.
        const currentSetId = this.$currentSetId()
        if (!currentSetId) return
        // Fast path: the set may already belong to a project whose sets we have loaded.
        const loadedOwner = this.$projects().find((project) => project.sets.some((set) => set.id === currentSetId))
        if (loadedOwner) {
            this.expandProject(loadedOwner)
            return
        }
        const {set} = await fetchThrowingErrors(this.setGql)({id: currentSetId})
        const newProject = this.getProjectNode(set.project.id)
        if (newProject) {
            this.expandProject(newProject)
        }
    }

    private async fetchProjects(organizationId: string | undefined) {
        if (!organizationId) return null
        try {
            return fetchThrowingErrors(this.projectsGql)({organizationId}).then((result) =>
                result.projects.filter(IsNonNull).map((project): ProjectTreeNode => ({...project, sets: [], setsLoaded: false})),
            )
        } catch {
            this.snackBar.open(`Cannot fetch project for organization id: ${organizationId}`, "", {duration: 3000})
        }
        return null
    }

    onToggleExpanded(item: ProjectTreeNode | ProjectTreeSetFragment, type: "project" | "set") {
        if (!this.treeControl.isExpanded(item)) {
            this.treeControl.toggleDescendants(item)
        }
        if (type === "set") {
            this.$currentProjectId.set(undefined)
            this.$currentSetId.set(item.id)
        } else {
            this.$currentProjectId.set(item.id)
            this.$currentSetId.set(undefined)
        }
    }

    readonly $expandedNodes = signal(new Set<string>())

    onExpandedChanged(change: SelectionChange<string>) {
        const newSet = new Set(this.$expandedNodes())
        change.added.forEach((nodeId) => {
            newSet.add(nodeId)
            // A newly expanded project needs its sets fetched (no-op for set nodes / already-loaded projects).
            const project = this.getProjectNode(nodeId)
            if (project) {
                void this.loadSetsForProject(project)
            }
        })
        change.removed.forEach((nodeId) => {
            newSet.delete(nodeId)
        })
        this.$expandedNodes.set(newSet)
    }

    // Fetches a project's sets on first expand and re-renders the tree with them.
    async loadSetsForProject(project: ProjectTreeNode) {
        const projectId = project.id
        // Resolve against the current list: the passed node may be a stale object from before a mutation.
        const current = this.$projects().find((candidate) => candidate.id === projectId)
        if (!current || current.setsLoaded || this.$loadingProjectIds().has(projectId)) {
            return
        }
        this.$loadingProjectIds.update((ids) => new Set(ids).add(projectId))
        try {
            const {sets} = await fetchThrowingErrors(this.projectSetsGql)({projectId})
            // Re-resolve by id and update immutably, in case the array was replaced while the fetch was in flight.
            this.$projects.set(this.$projects().map((node) => (node.id === projectId ? {...node, sets: sets.filter(IsNonNull), setsLoaded: true} : node)))
        } catch {
            this.snackBar.open(`Cannot load sets for project: ${project.name}`, "", {duration: 3000})
        } finally {
            this.$loadingProjectIds.update((ids) => {
                const next = new Set(ids)
                next.delete(projectId)
                return next
            })
        }
    }

    getProjectNode(id: string): ProjectTreeNode | undefined {
        return this.$projects().find((project) => project.id === id)
    }

    updateProjectName(projectNode: ProjectTreeNode) {
        const dialogRef = this.dialog.open(DialogRenameComponent, {
            disableClose: false,
            width: "400px",
            data: {
                title: projectNode.name,
            },
        })
        outputToObservable(dialogRef.componentInstance.onConfirm)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(async (title: string) => {
                try {
                    await mutateThrowingErrors(this.updateProjectGql)({input: {id: projectNode.id, name: title}})
                    this.$projects.set(this.$projects().map((project) => (project.id === projectNode.id ? {...project, name: title} : project)))
                    this.snackBar.open("Changes saved.", "", {duration: 3000})
                } catch {
                    this.snackBar.open("Cannot save changes.", "", {duration: 3000})
                }
            })
    }

    updateSetName(setNode: ProjectTreeSetFragment) {
        const dialogRef = this.dialog.open(DialogRenameComponent, {
            disableClose: false,
            width: "400px",
            data: {
                title: setNode.name,
            },
        })
        dialogRef.componentInstance.onConfirm.subscribe(async (title: string) => {
            try {
                await mutateThrowingErrors(this.updateSetGql)({input: {id: setNode.id, name: title}})
                this.$projects.set(
                    this.$projects().map((project) => ({
                        ...project,
                        sets: project.sets.map((set) => (set.id === setNode.id ? {...set, name: title} : set)),
                    })),
                )
                this.snackBar.open("Changes saved.", "", {duration: 3000})
            } catch {
                this.snackBar.open("Cannot save changes.", "", {duration: 3000})
            }
        })
    }

    addProject() {
        const dialogRef = this.dialog.open(DialogRenameComponent, {
            disableClose: false,
            width: "400px",
            data: {
                title: "",
            },
        })
        dialogRef.componentInstance.onConfirm.subscribe(async (title: string) => {
            try {
                const organizationId = this.$organization()?.id
                if (!organizationId) {
                    return
                }
                const {createProject} = await mutateThrowingErrors(this.createProjectGql)({
                    input: {
                        name: title,
                        organizationId,
                    },
                })
                // A freshly created project has no sets yet, so mark it loaded to skip a needless fetch.
                const project: ProjectTreeNode = {...createProject, sets: [], setsLoaded: true}
                this.$projects.set([project, ...(this.$projects() ?? [])])
                await this.router.navigate([], {
                    relativeTo: this.route,
                    queryParamsHandling: "merge",
                    queryParams: {organizationId: this.$organization()?.id, project: project.id},
                })
            } catch {
                this.snackBar.open("Cannot create project.", "", {duration: 3000})
            }
        })
    }

    addSet(projectId: string) {
        const dialogRef = this.dialog.open(DialogRenameComponent, {
            disableClose: false,
            width: "400px",
            data: {
                title: "",
            },
        })
        outputToObservable(dialogRef.componentInstance.onConfirm)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(async (title: string) => {
                try {
                    const {createSet: set} = await mutateThrowingErrors(this.createSetGql)({
                        input: {
                            name: title,
                            projectId: projectId,
                        },
                    })
                    this.$projects.set(
                        this.$projects()?.map((project) =>
                            project.id === projectId ? {...project, sets: [...project.sets, set], setsLoaded: true} : project,
                        ) ?? [],
                    )
                    const project = this.$projects().find((project) => project.id === projectId)
                    if (project) {
                        this.expandProject(project)
                    }
                    await this.router.navigate([], {
                        relativeTo: this.route,
                        queryParamsHandling: "merge",
                        queryParams: {organizationId: this.$organization()?.id, setId: set.id},
                    })
                } catch (error) {
                    this.logger.error("Cannot create set:", error)
                    this.snackBar.open("Cannot create set.", "", {duration: 3000})
                }
            })
    }

    async deleteProject(projectNode: ProjectTreeNode) {
        // Sets load lazily, so ensure we know this project's sets before guarding against a non-empty delete.
        if (!projectNode.setsLoaded) {
            await this.loadSetsForProject(projectNode)
        }
        // loadSetsForProject updates $projects immutably, so re-resolve the current node rather than reading the
        // (possibly stale) argument, whose sets would still be empty.
        const current = this.getProjectNode(projectNode.id) ?? projectNode
        if (current.sets.length) {
            this.snackBar.open("Cannot delete a project with sets, please delete the sets first.", "", {duration: 3000})
            return
        }
        try {
            await mutateThrowingErrors(this.deleteProjectGql)({id: projectNode.id})
            this.$projects.set(this.$projects()?.filter((project) => project.id !== projectNode.id) ?? [])
            this.treeControl.expansionModel.deselect(projectNode.id)
            if (this.$currentProjectId() === projectNode.id) {
                this.$currentProjectId.set(undefined)
            }
            this.snackBar.open("Project deleted.", "", {duration: 3000})
        } catch {
            this.snackBar.open("Cannot delete project.", "", {duration: 3000})
        }
    }

    async deleteSet(setNode: ProjectTreeSetFragment) {
        try {
            await mutateThrowingErrors(this.deleteSetGql)({id: setNode.id})
            this.$projects.set(this.$projects()?.map((node) => ({...node, sets: node.sets.filter((set) => set.id !== setNode.id)})) ?? [])
            if (this.$currentSetId() === setNode.id) {
                this.$currentSetId.set(undefined)
            }
            this.snackBar.open("Set deleted.", "", {duration: 3000})
        } catch {
            this.snackBar.open("Cannot delete set. Does it still contain pictures?", "", {duration: 3000})
        }
    }

    async setHighestPriority(setNode: ProjectTreeSetFragment) {
        try {
            const {pictures} = await fetchThrowingErrors(this.picturesGql)({
                take: 9999,
                filter: {setId: {equals: setNode.id}},
            })

            for (const picture of pictures.filter(IsNonNull)) {
                await mutateThrowingErrors(this.updatePictureGql)({input: {id: picture.id, priorityOrder: 0}})
            }

            this.snackBar.open("Changes saved.", "", {duration: 3000})
        } catch {
            this.snackBar.open("Cannot save changes.", "", {duration: 3000})
        }
    }

    protected readonly ThumbnailLayout = ThumbnailLayout
}
